import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const IMG = (id) =>
  `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100`;

const PRODUCTS = [
  {
    id: "boom-barrier",
    index: "01",
    name: "Boom Barriers",
    series: "RX-BB Series",
    desc: "Automatic electromechanical barriers for gates, tolls and townships.",
    specs: ["3–6 m arm", "1.5s lift cycle", "IP54 cabinet"],
    img: IMG("photo-1596125861210-f1b3c7b2f2f6"),
    span: "md:col-span-7",
  },
  {
    id: "speed-breaker",
    index: "02",
    name: "Speed Breakers",
    series: "RX-SB Series",
    desc: "Rubberised modular humps that survive heavy axle loads.",
    specs: ["50 mm profile", "25 T load rated", "Night-reflective"],
    img: IMG("photo-1560961821-9ccd27e77d8b"),
    span: "md:col-span-5",
  },
  {
    id: "sign-boards",
    index: "03",
    name: "Road Sign Boards",
    series: "RX-SG Series",
    desc: "Retro-reflective mandatory, cautionary & informatory signage.",
    specs: ["Type-IV sheeting", "ACP substrate"],
    img: IMG("photo-1685992830281-2eef1f9bd3e8"),
    span: "md:col-span-3",
  },
  {
    id: "delineators",
    index: "04",
    name: "Delineators",
    series: "RX-DL Series",
    desc: "Flexible posts and channelizers for lane discipline.",
    specs: ["PU rebound body", "750 mm height"],
    img: IMG("photo-1669206687431-057031e59053"),
    span: "md:col-span-3",
  },
  {
    id: "traffic-cones",
    index: "05",
    name: "Traffic Cones",
    series: "RX-TC Series",
    desc: "Heavy-base cones for work zones and diversions.",
    specs: ["750 mm", "4.5 kg base"],
    img: IMG("photo-1700589098064-019d9d285fd5"),
    span: "md:col-span-3",
  },
  {
    id: "reflective-studs",
    index: "06",
    name: "Reflective Studs",
    series: "RX-RS Series",
    desc: "Aluminium & ABS cat-eye road studs, epoxy anchored.",
    specs: ["20 T compressive", "360° visibility"],
    img: IMG("photo-1529895131601-d958f6da1f20"),
    span: "md:col-span-3",
  },
];

const Card = ({ p, i }) => (
  <motion.a
    href="#contact"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative overflow-hidden border border-zinc-800 hover:border-amber-500/50 transition-colors duration-500 ${p.span} h-[320px] md:h-[380px] block`}
    data-testid={`product-card-${p.id}`}
  >
    <img
      src={p.img}
      alt={p.name}
      className="absolute inset-0 w-full h-full object-cover opacity-55 transition-all duration-700 group-hover:opacity-75 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/45 to-transparent" />

    <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-amber-500/70" />
    <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-amber-500/70" />
    <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-amber-500/70" />
    <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-amber-500/70" />

    <div className="relative h-full flex flex-col justify-between p-6">
      <div className="flex items-start justify-between">
        <span className="font-mono2 text-xs tracking-[0.3em] text-amber-400">{p.index}</span>
        <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-400 border border-zinc-700 px-2 py-1">
          {p.series}
        </span>
      </div>
      <div>
        <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-wide text-white flex items-center gap-2">
          {p.name}
          <ArrowUpRight className="w-5 h-5 text-amber-500 opacity-0 -translate-x-1 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" />
        </h3>
        <p className="text-sm text-zinc-300 mt-1 max-w-xs">{p.desc}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {p.specs.map((s) => (
            <span key={s} className="font-mono2 text-[10px] tracking-wider uppercase text-amber-300/90 bg-amber-500/10 border border-amber-500/25 px-2 py-1">
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  </motion.a>
);

export const Products = () => (
  <section id="products" className="relative py-24 lg:py-32 bg-[#0B0F17]" data-testid="products-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-wrap items-end justify-between gap-6 mb-14"
      >
        <div>
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-amber-400 mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-amber-500 inline-block" /> Product Matrix
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase text-white leading-none" data-testid="products-heading">
            Hardware That<br />Holds The <span className="text-amber-500">Line</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-zinc-400">
          Six engineered product lines. Manufactured in-house, batch-tested, and shipped
          in bulk to sites across India. Tap any unit to request a quotation.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {PRODUCTS.map((p, i) => (
          <Card key={p.id} p={p} i={i} />
        ))}
      </div>
    </div>
  </section>
);
